import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { FiTrash2, FiAlertTriangle, FiHelpCircle } from 'react-icons/fi';
import './ConfirmDialog.scss';

const VARIANT_ICONS = {
  danger: FiTrash2,
  warning: FiAlertTriangle,
  primary: FiHelpCircle,
};

/**
 * Reusable confirmation dialog (replacement for window.confirm).
 *
 * <ConfirmDialog
 *   open={!!pending}
 *   variant="danger"                  // 'danger' | 'warning' | 'primary'
 *   title={t('...')}
 *   message={t('...')}
 *   confirmLabel={t('common.delete')} // defaults to common.confirm
 *   loading={busy}                    // disables buttons while the action runs
 *   onConfirm={doIt}
 *   onCancel={() => setPending(null)}
 * />
 */
function ConfirmDialog({
  open,
  variant = 'danger',
  icon,
  title,
  message,
  confirmLabel,
  cancelLabel,
  loading = false,
  onConfirm,
  onCancel,
}) {
  const { t } = useTranslation();
  const cancelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    // Focus the safe action so Enter doesn't confirm a destructive op by accident.
    cancelRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape' && !loading) onCancel?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, loading, onCancel]);

  if (!open) return null;

  const Icon = icon || VARIANT_ICONS[variant] || FiHelpCircle;

  return createPortal(
    <div
      className="confirm-dialog__overlay"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !loading) onCancel?.();
      }}
    >
      <div
        className={`confirm-dialog confirm-dialog--${variant}`}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-message"
      >
        <div className="confirm-dialog__icon" aria-hidden="true">
          <Icon />
        </div>
        <h3 id="confirm-dialog-title" className="confirm-dialog__title">{title}</h3>
        {message && (
          <p id="confirm-dialog-message" className="confirm-dialog__message">{message}</p>
        )}
        <div className="confirm-dialog__actions">
          <button
            type="button"
            ref={cancelRef}
            className="btn btn-outline"
            disabled={loading}
            onClick={onCancel}
          >
            {cancelLabel || t('common.cancel')}
          </button>
          <button
            type="button"
            className={`btn ${variant === 'danger' ? 'btn-danger' : 'btn-primary'}`}
            disabled={loading}
            onClick={onConfirm}
          >
            {loading && <span className="confirm-dialog__spinner" aria-hidden="true" />}
            {confirmLabel || t('common.confirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export default ConfirmDialog;
